import { DEFAULT_USER_CONFIG } from '@cronos-app/swap-sdk';
import { Fraction } from 'bi-fraction';
import { isAddress } from 'ethers';
import { ERC20__factory, TopupRouter__factory, TopupWithH2Router__factory, } from '../contracts';
import { SmartRouterService } from '../implements/SmartRouterService';
import { SwapService } from '../implements/SwapService';
import { getContract } from '../utils/getContract';
import { getErc20Approval } from '../utils/getErc20Approval';
import { SWAP_AND_TOPUP_SC_BY_CHAIN, TOPUP_SC_BY_CHAIN } from './config';
export class CdcDepositCardService {
    constructor(chainId, 
    // for test
    enableSwapAndTopUp, clientId) {
        this.chainId = chainId;
        this.userConfig = DEFAULT_USER_CONFIG;
        this.bindCard = (signer, depositAddress) => this.getter_topupRouterSc(signer).register(depositAddress);
        this.unbindCard = (signer) => this.getter_topupRouterSc(signer).unregister();
        this.getCardAddress = (account) => this.getter_topupRouterSc().depositAddresses(account);
        this.validateDepositAddress = (address) => isAddress(address);
        this.setUserConfig = (config) => {
            this.userConfig = {
                ...DEFAULT_USER_CONFIG,
                ...config,
            };
        };
        this.exec_swapAndTopUp = async (info, signer, holdFund) => {
            const account = await signer.getAddress();
            let toAmount = info.to.amount;
            if (info.needToSwapFirst) {
                if (this.swapAndTopUpScAddr)
                    return this.exec_swapAndTopUpInOneTx(account, info, signer, holdFund);
                // swap first
                toAmount = await this.exec_swap(account, info, signer);
            }
            // top up
            return this.exec_topUp({
                ...info.to,
                amount: toAmount,
            }, signer);
        };
        this.exec_swapAndTopUpInOneTx = async (account, info, signer, holdFund) => {
            // swap and top up in one tx
            const args = this.calc_swapAndTopUpArgs(holdFund ? this.swapAndTopUpScAddr : account, info);
            if (!args.value) {
                // input is not native token, need to check allowance
                const inApproval = getErc20Approval(this.chainId, args.inputTokenWithAmount.address, account, this.swapAndTopUpScAddr);
                if (await inApproval.isNeedToApprove(args.inputTokenWithAmount.amount)) {
                    await inApproval.approve(args.inputTokenWithAmount.amount, signer);
                }
            }
            if (!holdFund) {
                const outApproval = getErc20Approval(this.chainId, args.outputToken.address, account, this.swapAndTopUpScAddr);
                // notice: we don't know the exact amount of output token, for now, we just approve 10x of expected amount
                const outputExpectedAllowance = (BigInt(args.swapInfo.executionContext.trade.amountOut.amount) * 10n).toString();
                if (await outApproval.isNeedToApprove(outputExpectedAllowance)) {
                    await outApproval.approve(outputExpectedAllowance, signer);
                }
            }
            const method = this.getter_swapAndTopupRouterSc(signer).swapAndDeposit;
            return method(args.inputTokenWithAmount.address, account, args.inputTokenWithAmount.amount, args.outputToken.address, args.outputTokenOfCalldatas, args.calldatas, {
                value: args.value,
            });
        };
        this.exec_swap = async (recipient, info, signer) => {
            if (await this.swapService.isApproveNeeded(recipient, info.from)) {
                await this.swapService.approve(info.from, signer);
            }
            const tx = await this.swapService.swap_exactInput(info.executionContext.trade, info.executionContext.opts, recipient, signer);
            let receivedAmount = BigInt(0);
            const res = await tx.wait();
            for (const log of res?.logs ?? []) {
                const transfer = ERC20__factory.createInterface().parseLog(log);
                const from = transfer?.args[0];
                const to = transfer?.args[1];
                if (!transfer ||
                    typeof from !== 'string' ||
                    from.toLowerCase() ===
                        this.swapService.gasRefundContract.toLowerCase() ||
                    typeof to !== 'string' ||
                    to.toLowerCase() !== recipient.toLowerCase())
                    continue;
                if (typeof transfer.args[2] === 'bigint') {
                    receivedAmount += transfer.args[2];
                }
            }
            return new Fraction(receivedAmount).toFixed(0);
        };
        this.exec_topUp = async (to, signer) => {
            const account = await signer.getAddress();
            const { isNeedToApprove, approve } = getErc20Approval(this.chainId, to.address, account, this.topUpScAddr);
            if (await isNeedToApprove(to.amount)) {
                await approve(to.amount, signer);
            }
            return this.getter_topupRouterSc(signer).deposit(to.address, account, to.amount);
        };
        this.estimateGas_swapAndTopUp = async (recipient, info, signer, holdFund) => {
            let gas = 0n;
            if (info.needToSwapFirst) {
                if (this.swapAndTopUpScAddr) {
                    try {
                        gas = await this.estimateGas_swapAndTopUpInOneTx(recipient, info, signer, holdFund);
                        return gas;
                    }
                    catch (err) {
                        // notice: throw error when input/output allowance is not enough
                        // in this case, fallback to split tx
                    }
                }
                // fallback to split tx
                gas += await this.estimateGas_swap(recipient, info, signer);
            }
            gas += await this.estimateGas_topUp(recipient, info.to, signer);
            return gas;
        };
        this.estimateGas_topUp = async (recipient, to, signer) => {
            let gas = 0n;
            try {
                gas = await this.getter_topupRouterSc(signer).deposit.estimateGas(to.address, recipient, to.amount);
            }
            catch (err) {
                // notice: throw error when input/output allowance is not enough
                // in this case, we use hardcode gas
                gas = 135000n;
            }
            return gas;
        };
        this.estimateGas_swap = async (recipient, info, signer) => {
            let gas = 0n;
            try {
                gas = await this.swapService.swap_exactInput_estimateGas(info.executionContext.trade, info.executionContext.opts, recipient, signer);
            }
            catch (err) {
                // notice: throw error when input/output allowance is not enough
                // in this case, we use hardcode gas
                // info.executionContext.trade.routes.
                const { splittedPaths } = this.calc_swapAndTopUpArgs(recipient, info);
                gas = splittedPaths.reduce((a, p) => a + 135000n + BigInt(p.v2Path.length) * 50000n, 0n);
            }
            return gas;
        };
        this.estimateGas_swapAndTopUpInOneTx = (recipient, info, signer, holdFund) => {
            const args = this.calc_swapAndTopUpArgs(holdFund ? this.swapAndTopUpScAddr : recipient, info);
            const method = this.getter_swapAndTopupRouterSc(signer).swapAndDeposit;
            return method.estimateGas(args.inputTokenWithAmount.address, recipient, args.inputTokenWithAmount.amount, args.outputToken.address, args.outputTokenOfCalldatas, args.calldatas, {
                value: args.value,
            });
        };
        this.calc_swapAndTopUpArgs = (recipient, swapInfo) => {
            const { calldatas, value, splittedPaths } = this.swapService.getCalldatasOfSwap_exactInput(swapInfo.executionContext.trade, swapInfo.executionContext.opts, recipient);
            return {
                swapInfo,
                inputTokenWithAmount: {
                    ...swapInfo.executionContext.opts.from,
                    amount: swapInfo.executionContext.opts.amount,
                },
                outputToken: swapInfo.executionContext.opts.to,
                calldatas,
                value,
                outputTokenOfCalldatas: splittedPaths.map((p) => p.v2Path[p.v2Path.length - 1]),
                splittedPaths,
            };
        };
        this.getter_topupRouterSc = (signer) => {
            if (!this.topUpScAddr)
                throw new Error('TopUpRouter not found');
            return getContract(this.chainId, this.topUpScAddr, TopupRouter__factory.abi, signer);
        };
        this.getter_swapAndTopupRouterSc = (signer) => {
            if (!this.swapAndTopUpScAddr)
                throw new Error('SwapAndTopUpRouter not found');
            return getContract(this.chainId, this.swapAndTopUpScAddr, TopupWithH2Router__factory.abi, signer);
        };
        this.getter_swapAndTopUpArgs = async (recipient, from, to) => {
            return this.calc_swapAndTopUpArgs(recipient, await this.getter_bestSwapInfo(from, [to], {}));
        };
        this.getter_swapAndTopUpInfo = async (from, whitelistToTokens, whitelistToTokenPrices) => {
            if (whitelistToTokens.map((t) => t.address).includes(from.address))
                return {
                    needToSwapFirst: false,
                    from,
                    to: from,
                };
            return this.getter_bestSwapInfo(from, whitelistToTokens, whitelistToTokenPrices);
        };
        this.getter_bestSwapInfo = async (from, whitelistToTokens, whitelistToTokenPrices) => {
            const res = await Promise.all(whitelistToTokens.map((to) => this.smartRouterService.getSwapInfo(from, to, this.userConfig)));
            const usdAmounts = res.map((cur, i) => {
                const price = whitelistToTokenPrices[whitelistToTokens[i].address];
                if (!cur || !price)
                    return new Fraction(0);
                return new Fraction(cur.info.receivedMin.amount)
                    .shr(cur.info.receivedMin.decimals)
                    .mul(price);
            });
            let best = 0;
            for (let i = 1; i < res.length; i += 1) {
                if (usdAmounts[i].gt(usdAmounts[best])) {
                    best = i;
                }
            }
            const pickedInfo = res[best];
            if (!pickedInfo)
                throw new Error('No route found');
            return {
                needToSwapFirst: true,
                from,
                to: {
                    ...whitelistToTokens[best],
                    amount: pickedInfo.args.amountOut.amount,
                },
                executionContext: {
                    trade: pickedInfo.args,
                    opts: pickedInfo.opts,
                    extra: pickedInfo.info,
                },
            };
        };
        this.smartRouterService = new SmartRouterService(chainId, clientId);
        this.swapService = new SwapService(chainId);
        this.topUpScAddr = TOPUP_SC_BY_CHAIN[chainId];
        this.swapAndTopUpScAddr = enableSwapAndTopUp
            ? SWAP_AND_TOPUP_SC_BY_CHAIN[chainId]
            : '';
    }
}
